import { MutationTree } from 'vuex';
import { IDocumentViewState } from '@/store/stores/document-view.store';
import { IDocumentTracking } from '@/document-tracking/types/document-tracking';
import { IDocument } from '@/types/Document';
import { IRecipient } from '@/types/Recipient';
import { ISender } from '@/types/Sender';
import { IPreviewToken } from '@/dealroom/document/types/preview-token';
import { IClickTracking } from '@/document-tracking/types/click-tracking';
import { IPageTracking } from '@/document-tracking/types/page-tracking';
import { IVideoTracking } from '@/document-tracking/types/video-tracking';
import { IVisitTracking } from '@/document-tracking/types/visit-tracking';
import { ITag } from '@/types/Tag';
import { IDocumentEventItem } from '@/document-view/types/document-events-item';

export const mutationSetDocumentData = 'setDocumentData';
export const mutationSetLoading = 'setLoading';
export const mutationLoadingRecipients = 'setLoadingRecipient';
export const mutationSetRecipients = 'setRecipients';
export const mutationSetSender = 'setSender';
export const mutationSetTracking = 'setTracking';
export const mutationSetDocumentSender = 'setSender';
export const mutationSetState = 'setState';
export const mutationSetSelectedRecipientId = 'setSelectedRecipientId';
export const mutationSetPreviewToken = 'setPreviewToken';
export const mutationSetCancelScheduleModalVisibility = 'setCancelScheduleModalVisibility';

export const mutationSetTrackClickUpdate = 'setTrackClickUpdate';
export const mutationSetTrackVideoUpdate = 'setTrackVideoUpdate';
export const mutationSetTrackPageUpdate = 'setTrackPageUpdate';
export const mutationSetTrackVisitUpdate = 'setTrackVisitUpdate';

export const mutationAddDocumentTag = 'addDocumentTag';
export const mutationSetDocumentTags = 'addDocumentTags';
export const mutationSetDocumentEvents = 'setDocumentEvents';
export const mutationRemoveDocumentTag = 'removeDocumentTag';

export const mutations: MutationTree<IDocumentViewState> = {
  [mutationSetDocumentData](state, payload: IDocument) {
    state.isSigning = payload.isSigning;
    state.documentId = payload.id;
    state.isSigningOrder = payload.isSigningOrder;
    state.document = payload;
  },
  [mutationSetLoading](state, loading: boolean) {
    state.isLoadingDocument = loading;
  },
  [mutationSetRecipients](state, payload: IRecipient[]) {
    state.recipients = payload;
  },
  [mutationSetSender](state, payload: ISender) {
    state.sender = payload;
  },
  [mutationLoadingRecipients](state, payload: boolean) {
    state.isLoadingRecipient = payload;
  },
  [mutationSetTracking](state: IDocumentViewState, tracking: IDocumentTracking) {
    state.tracking = { ...tracking };
  },
  [mutationSetState](state: IDocumentViewState, newState: IDocumentViewState) {
    state.documentId = newState.documentId;
    state.isSigning = newState.isSigning;
    state.isSigningOrder = newState.isSigningOrder;
    state.isLoadingDocument = newState.isLoadingDocument;
    state.isLoadingRecipient = newState.isLoadingRecipient;
    state.recipients = newState.recipients;
    state.sender = newState.sender;
    state.tracking = newState.tracking;
    state.selectedRecipientId = newState.selectedRecipientId;
    state.previewToken = newState.previewToken;
    state.modals = newState.modals;
    state.events = newState.events;
  },
  [mutationSetSelectedRecipientId](state: IDocumentViewState, recipientId: string) {
    state.selectedRecipientId = recipientId;
  },
  [mutationSetPreviewToken](state: IDocumentViewState, previewToken: IPreviewToken) {
    state.previewToken = previewToken;
  },
  [mutationSetCancelScheduleModalVisibility](state: IDocumentViewState, show: boolean) {
    state.modals.showCancelSchedule = show;
  },
  [mutationSetTrackClickUpdate](state: IDocumentViewState, trackClickUpdate: IClickTracking) {
    state.tracking.clickTrackings = [...state.tracking.clickTrackings, trackClickUpdate];
  },
  [mutationSetTrackVideoUpdate](state: IDocumentViewState, trackVideoUpdate: IVideoTracking) {
    state.tracking.videoTrackings = [...state.tracking.videoTrackings, trackVideoUpdate];
  },
  [mutationSetTrackPageUpdate](state: IDocumentViewState, updatedPageTrackings: IPageTracking[]) {
    state.tracking.pageTrackings = updatedPageTrackings;
  },
  [mutationSetTrackVisitUpdate](state: IDocumentViewState, trackVisitUpdate: IVisitTracking) {
    state.tracking.visitTrackings = [...state.tracking.visitTrackings, trackVisitUpdate];
  },
  [mutationAddDocumentTag](state: IDocumentViewState, newTag: ITag) {
    state.document.tags = state.document.tags ? [...state.document.tags, newTag] : [newTag];
  },
  [mutationSetDocumentTags](state: IDocumentViewState, tags: ITag[]) {
    state.document.tags = tags;
  },
  [mutationSetDocumentEvents](state: IDocumentViewState, events: IDocumentEventItem[]) {
    state.events = events;
  },
  [mutationRemoveDocumentTag](state: IDocumentViewState, tagId: string) {
    state.document.tags = state.document.tags.filter((tag: ITag) => tag.id !== tagId);
  },
};
